import Paths from 'config/paths';
import { Menus } from 'config/menus';
import { defaultQueries } from 'utils/helpers/helper';
import { ADMIN_CATEGORIES, buildClientCategories, LOT_PATHS } from './categories';

// Tipos de sección que el SideBar sabe renderizar.
export const SECTION = Object.freeze({
  BALANCE: 'balance', // bloque "Saldo disponible"
  GOTO_LIVE: 'gotoLive', // botón "Ir al remate"
  NOVEDADES: 'novedades', // ítems marcados con (NUEVO)
  PROFILE_ACTIONS: 'profileActions', // Mi Perfil + Cerrar sesión
  AUTH_ACTIONS: 'authActions', // Iniciar Sesión + Registrarse
  COLLAPSIBLE: 'collapsible', // encabezado colapsable + paths
  FLAT: 'flat', // lista plana de paths, sin encabezado ni colapso
});

// Paths que se renderizan aparte (no deben aparecer en secciones de lista).
const RESERVED_PATHS = new Set([Paths.PROFILE, Paths.LOGIN, Paths.REGISTER]);

// "Menú" = todas las keys de Menus que no están en ninguna categoría ni
// reservadas. Recibe el conjunto de paths ya categorizados para excluirlos.
const buildMenuPaths = (categorizedPaths) =>
  Object.keys(Menus).filter(
    (path) => !RESERVED_PATHS.has(path) && !categorizedPaths.has(path),
  );

const pathsOf = (categories) => {
  const set = new Set();
  categories.forEach((cat) => cat.paths.forEach((p) => set.add(p)));
  return set;
};

const categoriesToSections = (categories) =>
  categories.map((cat) => ({ type: SECTION.COLLAPSIBLE, title: cat.label, paths: cat.paths }));

// --- ADMIN ---
// Perfil+logout y las categorías en orden admin, con "Menú" intercalado JUSTO
// debajo de Remates. Los admins NO tienen bloque de saldos (exclusivo cliente).
export function adminSections() {
  const categorized = pathsOf(ADMIN_CATEGORIES);
  const menuSection = {
    type: SECTION.COLLAPSIBLE,
    title: 'Menú',
    paths: buildMenuPaths(categorized),
    withExternal: true,
  };
  // Inserta el Menú después de la categoría "Remates".
  const categorySections = [];
  categoriesToSections(ADMIN_CATEGORIES).forEach((sec) => {
    categorySections.push(sec);
    if (sec.title === 'Remates') categorySections.push(menuSection);
  });
  return [
    { type: SECTION.GOTO_LIVE },
    { type: SECTION.PROFILE_ACTIONS },
    ...categorySections,
  ];
}

// --- CLIENTE (CLI) ---
// Remates primero; luego el Menú (ordenado alfabéticamente, con los Lotes que
// salen de Administración); luego el resto de categorías.
export function clientSections() {
  const clientCategories = buildClientCategories(); // Remates ya viene primero
  const categorized = pathsOf(clientCategories); // sin los LOT_PATHS
  const menuPaths = [...buildMenuPaths(categorized), ...LOT_PATHS];
  const menuSection = {
    type: SECTION.COLLAPSIBLE,
    title: 'Menú',
    paths: menuPaths,
    withExternal: true,
    sortAlpha: true,
  };
  // Inserta el Menú justo después de la categoría "Remates".
  const categorySections = [];
  categoriesToSections(clientCategories).forEach((sec) => {
    categorySections.push(sec);
    if (sec.title === 'Remates') categorySections.push(menuSection);
  });
  return [
    { type: SECTION.BALANCE },
    { type: SECTION.GOTO_LIVE },
    { type: SECTION.PROFILE_ACTIONS },
    ...categorySections,
  ];
}

// --- GUEST (sin sesión) ---
// Auth al inicio y luego TODO plano (sin submenús/colapsables), en el orden:
// Remates → Lotes → Portal Mandantes y Preguntas Frecuentes al final.
export function guestSections() {
  const flatPaths = [
    // Remates
    Paths.AUCTIONS + defaultQueries.fortinures.all,
    Paths.AUCTIONS + defaultQueries.properties.all,
    `${Paths.FINISHED_AUCTIONS.replace(':areaID', '1')}?&page=1&limit=10`,
    // Lotes
    LOT_PATHS[0],
    LOT_PATHS[1],
    // Al final
    Paths.MANDATES_LOGIN,
    Paths.FAQ,
  ];
  return [
    { type: SECTION.AUTH_ACTIONS },
    { type: SECTION.FLAT, paths: flatPaths },
  ];
}

// Dispatcher: elige el set de secciones según el usuario.
export function sectionsForUser({ hasUser, isClient }) {
  if (!hasUser) return guestSections();
  if (isClient) return clientSections();
  return adminSections();
}
